// ===== Preventative Maintenance Module Types =====

import type {
    PMConfiguration as Gen2PMConfiguration,
    PMServiceLevel as Gen2PMServiceLevel,
    PMPartsAndProcedure as Gen2PMPartsAndProcedure,
    LaborRate as Gen2LaborRate,
} from './schema';

/**
 * PM status classification for enrolled equipment units.
 * Status is computed client-side from UM counter values and thresholds.
 */
export enum PMStatus {
    OVERDUE = "Overdue",
    DUE_SOON = "Due Soon",
    SCHEDULED = "Scheduled/OK",
    COMPLETED = "Completed",
}

// ----- GraphQL Model Interfaces -----

/**
 * PMConfiguration: Gen2 base (timestamps relaxed to optional for client construction)
 * plus client-side denormalized relations that are NOT columns on the Gen2 model —
 * they're aggregated on the client from separate queries.
 */
export type PMConfiguration = Omit<Gen2PMConfiguration, 'createdAt' | 'updatedAt'> & {
    createdAt?: string;
    updatedAt?: string;
    /** Denormalized client-side: loaded via listPMServiceLevelByConfig, not a Gen2 relation column. */
    serviceLevels?: PMServiceLevel[] | null;
    /** Denormalized client-side. */
    partsAndProcedures?: PMPartsAndProcedure[] | null;
    /** Denormalized client-side. */
    notificationSettings?: PMNotificationSetting[] | null;
};

export type PMServiceLevel = Omit<Gen2PMServiceLevel, 'createdAt' | 'updatedAt'> & {
    createdAt?: string;
    updatedAt?: string;
};

export type PMPartsAndProcedure = Omit<Gen2PMPartsAndProcedure, 'createdAt' | 'updatedAt'> & {
    createdAt?: string;
    updatedAt?: string;
    /** Client-only audit trail (JSON string); not a Gen2 column. */
    editHistory?: string | null;
};

export interface PMNotificationSetting {
    id: string;
    pmConfigurationId: string;
    serviceLevelId?: string | null;
    emailThresholdUM: number;
    notificationFrequency: string;
    recipients?: string[] | null;
    lastNotificationSent?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

// ----- Company-wide Labor Rate Types -----

/**
 * Company-wide labor rate category (e.g., "Arizona - Journeyman").
 * Groups historical LaborRate records.
 */
export interface LaborCategory {
    id: string;
    companyId: string;
    name: string;
    isActive: boolean;
    createdAt?: string;
    updatedAt?: string;
}

/**
 * Immutable historical labor rate record with an effective date.
 * The most recent rate where effectiveDate <= today is the current rate.
 */
export type LaborRate = Omit<Gen2LaborRate, 'createdAt' | 'updatedAt'> & {
    createdAt?: string;
    updatedAt?: string;
};

export interface PMReport {
    id: string;
    companyId: string;
    unitId: string;
    pmConfigurationId: string;
    serviceLevelId: string;
    serviceLevelName?: string | null;
    actualUM: number;
    serviceDate: string;
    technicianId: string;
    technicianName?: string | null;
    laborRateScheduleId?: string | null;
    laborRateScheduleName?: string | null;
    laborHoursST?: number | null;
    laborHoursOT?: number | null;
    laborRateST?: number | null;
    laborRateOT?: number | null;
    totalLaborCost?: number | null;
    reportData?: string | null; // AWSJSON stored as string
    issueDescription?: string | null;
    issueTicketCreated?: boolean | null;
    issueTicketId?: string | null;
    signature?: string | null;
    photos?: string[] | null;
    status?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

// ----- PM Template Types -----

export interface PMTemplateLevel {
    levelIdentifier: string;
    intervalValue: number;
    targetUM: number;
    description?: string;
}

export interface PMTemplateRow {
    itemName: string;
    itemType: 'consumable' | 'procedure';
    category?: string;
    manufacturer?: string;
    partNumber?: string;
    vendorName?: string;
    price?: number;
    quantity?: number;
    unitOfMeasure?: string;
}

export interface PMTemplateMatrixData {
    levels: PMTemplateLevel[];
    rows: PMTemplateRow[];
    /** assignments[levelIndex][rowIndex] = true/false or quantity */
    assignments: Record<string, Record<string, boolean | number>>;
}

export interface PMTemplate {
    id: string;
    companyId: string;
    categoryId: string;
    categoryName: string;
    name: string;
    description?: string | null;
    matrixData: PMTemplateMatrixData;
    createdByUserId?: string | null;
    createdByUserName?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

// ----- Input / Computation Types -----

export interface PMStatusInput {
    currentUM: number;
    targetUM: number;
    /** Share of the service interval. 0.1 on a 500 interval is the last 50 UM, not 10% of the target reading. */
    dueSoonPercentage: number;
    /** Level 1 interval. Due Soon is this interval times dueSoonPercentage. Falls back to targetUM. */
    intervalUM?: number | null;
}

export interface LaborCostInput {
    laborHoursST: number;
    laborHoursOT: number;
    standardRate: number;
    overtimeRate: number;
}

/**
 * Service level assignments map: partsAndProcedureId -> boolean (procedure) or quantity (consumable).
 * Stored as AWSJSON on each PMServiceLevel.
 */
export type ServiceLevelAssignments = Record<string, boolean | number>;

/**
 * Denormalized assignment matrix for the UI.
 * Rows = parts/procedures, columns = service levels, assignments = [partId][levelId] = checked.
 */
export interface AssignmentMatrix {
    rows: PMPartsAndProcedure[];
    columns: PMServiceLevel[];
    assignments: Record<string, Record<string, boolean>>; // [partId][levelId]
}

// ----- Cascading Service Info -----

/**
 * Represents the dynamically computed next service point and all levels due at that point.
 * Replaces the previous single-level return from getNextServiceLevel.
 */
export interface NextServiceInfo {
    targetUM: number;
    dueLevels: PMServiceLevel[];
}

// ----- Context Type -----

export interface PMContextType {
    pmConfigurations: PMConfiguration[];
    enrolledUnits: unknown[]; // Will reference SingleUnit from API.ts at integration time
    isLoading: boolean;
    selectedUnit: unknown | null;
    selectedConfig: PMConfiguration | null;
    setSelectedUnit: (unit: unknown | null) => void;
    refreshPMData: () => Promise<void>;
    getStatusForUnit: (unitId: string) => PMStatus;
    getNextServiceLevel: (configId: string) => NextServiceInfo | null;
}
